import React, { Fragment, useEffect, useState } from "react";
import { bool, func, object } from "prop-types";

import { connect } from "react-redux";

import UkModulusChecking from "uk-modulus-checking";

import { updateUserInProfileSilent } from "../../redux/actions";

import history from "../../history";

import { isEmpty } from "../../helpers/utils";

import { Button, Loading, Validate } from "../../common";

import { PayrollForm } from "../../components/BasicInfo";
import PayrollUpdateForm from "../../components/BasicInfo/Payroll/PayrollUpdateForm";
import * as styles from "./styles.module.scss";

const constraints = {
  accountNumber: {
    longerThanOrBlank: {
      number: 7,
      message:
        "^8 digits only, if bank account number is less than 8 digits, please input 0 at the start of the number",
    },
    length: { maximum: 8 },
    format: {
      pattern: /^[0-9]*$/,
      message:
        "^8 digits only, if bank account number is less than 8 digits, please input 0 at the start of the number",
    },
  },
  sortCode: {
    longerThanOrBlank: {
      number: 5,
      message: "^Sort code is required (6 digits only)",
    },
    format: {
      pattern: /^[0-9]*$/,
      message: "^Sort code is required (6 digits only)",
    },
  },
  nationalInsuranceNo: {
    format: {
      // https://regex101.com/r/O9V9lq/2
      pattern: /^(([A-za-z]{2}[0-9]{6}[A-Za-z])|)$/,
      message: "^Valid number need to be 9 characters",
    },
  },
  bankName: {
    isNotBlank: {},
  },
  accountName: {
    isNotBlank: {},
  },
};

const submitProfileUpdate = ({ payrollState, updateProfile, appData }) => {
  const { candidateRef, emailAddress } = appData;
  const updatedUserData = {
    candidateRef,
    emailAddress,
    ...payrollState,
  };
  Promise.all([
    updateProfile(candidateRef, updatedUserData),
    history.push("/profile"),
  ]);
};

/**
 * @param {Object} appData
 * @returns {[Object, Function]}
 */
const usePayrollState = (appData) => {
  const initialState = {
    bankName: appData.bankName || "",
    accountNumber: appData.accountNumber || "",
    sortCode: appData.sortCode || "",
    accountName: appData.accountName || "",
    nationalInsuranceNo: appData.nationalInsuranceNo || "",
    dbsConsent: appData.dbsConsent,
    taxTypeEmployeeStatement: appData.taxTypeEmployeeStatement || "",
  };

  const [blurredElements, setBlurredElements] = useState({});
  const setBlurred = (elementName) => {
    setBlurredElements((elements) => ({ ...elements, [elementName]: true }));
  };

  const mandatoryFieldsNames = Object.keys(initialState)
    .filter((key) => key !== "dbsConsent")
    .filter((key) => key !== "nationalInsuranceNo")
    .filter((key) => isEmpty(initialState[key]));

  const [payrollState, setPayrollState] = useState(initialState);

  const handleChange = (key, val) => {
    return setPayrollState({ ...payrollState, [key]: val });
  };

  return [
    payrollState,
    handleChange,
    mandatoryFieldsNames,
    blurredElements,
    setBlurred,
  ];
};

const TAX_TYPE_OPTIONS = [
  {
    value: "A",
    title: "Statement A",
    info: (
      <Fragment>
        <p>
          Do not choose this statement if you’re in receipt of a State, Works or
          Private Pension.
        </p>
        <p>Choose this statement if the following applies.</p>
      </Fragment>
    ),
    text: (
      <Fragment>
        <p>
          This is my first job since 6 April and since the 6 April I’ve not
          received payments from any of the following:{" "}
        </p>
        <ul>
          <li>Jobseeker’s Allowance</li>
          <li>Employment and Support Allowance</li>
          <li>Incapacity Benefit</li>
        </ul>
      </Fragment>
    ),
  },
  {
    value: "B",
    title: "Statement B",
    info: (
      <Fragment>
        <p>
          Do not choose this statement if you’re in receipt of a State, Works or
          Private Pension.
        </p>
        <p>Choose this statement if the following applies.</p>
      </Fragment>
    ),
    text: (
      <Fragment>
        <p>
          Since 6 April I have had another job but I do not have a P45. {"\n"}{" "}
          And/or since the 6 April I have received payments from any of the
          following:
        </p>
        <ul>
          <li>Jobseeker’s Allowance</li>
          <li>Employment and Support Allowance</li>
          <li>Incapacity Benefit</li>
        </ul>
      </Fragment>
    ),
  },
  {
    value: "C",
    title: "Statement C",
    info: (
      <Fragment>
        <p>Choose this statement if the following applies: </p>
      </Fragment>
    ),
    text: (
      <Fragment>
        <ul>
          <li>I have another job and/or</li>
          <li>I am in receipt of a State, Works or Private Pension</li>
        </ul>
      </Fragment>
    ),
  },
];

const Payroll = ({ loading, appData, updateProfile }) => {
  const [
    payrollState,
    handleChange,
    mandatoryFieldsNames,
    blurredElements,
    setBlurred,
  ] = usePayrollState(appData);
  const {
    bankName,
    accountNumber,
    sortCode,
    accountName,
    nationalInsuranceNo,
    dbsConsent,
    taxTypeEmployeeStatement,
  } = payrollState;

  const onSubmit = (e) => {
    e.preventDefault();
    submitProfileUpdate({ payrollState, appData, updateProfile });
  };

  const [validUkAccSortCode, setValidUkAccSortCode] = useState(false);

  useEffect(() => {
    const modulusCheck = new UkModulusChecking({
      accountNumber,
      sortCode,
    });
    setValidUkAccSortCode(modulusCheck.isValid());
  }, [accountNumber, sortCode]);

  const isUpdating = mandatoryFieldsNames.length === 0;
  const isDBSConsentVisible = appData.responsibleTeam === "LIVR";

  return (
    <Validate constraints={constraints} userInput={payrollState}>
      {({ validation }) => {
        const hasErrors = Object.keys(validation).length > 0;
        return (
          <div className="p-1 p-sm-0">
            <header
              className="row justify-content-center mb-1"
              style={{
                color: "#495057",
                letterSpacing: "0.6px",
                lineHeight: "19px",
              }}
            >
              <h2 className="mb-2 mt-2 col-md-7 col-sm-10">
                Your payroll information
              </h2>
              <span className="col-md-7 col-sm-10">
                If you have a Student Loan or a Post Graduate Loan please
                complete a full HMRC Starter Checklist, available online or
                through your consultant.
              </span>
            </header>
            <div className="row justify-content-center">
              <div className="col-md-7 col-sm-10">
                {isUpdating ? (
                  <PayrollUpdateForm
                    isDBSConsentVisible={isDBSConsentVisible}
                    bankName={bankName}
                    accountNumber={accountNumber}
                    sortCode={sortCode}
                    accountName={accountName}
                    nationalInsuranceNo={nationalInsuranceNo}
                    dbsConsent={dbsConsent}
                    taxTypeEmployeeStatement={taxTypeEmployeeStatement}
                    handleChange={handleChange}
                    validation={validation}
                    onSubmit={onSubmit}
                    loading={loading}
                    taxTypeOptions={TAX_TYPE_OPTIONS}
                    blurredElements={blurredElements}
                    setBlurred={setBlurred}
                    hasErrors={hasErrors}
                    validUkAccSortCode={validUkAccSortCode}
                  />
                ) : (
                  <PayrollForm
                    isDBSConsentVisible={isDBSConsentVisible}
                    bankName={bankName}
                    accountNumber={accountNumber}
                    sortCode={sortCode}
                    accountName={accountName}
                    nationalInsuranceNo={nationalInsuranceNo}
                    dbsConsent={dbsConsent}
                    taxTypeEmployeeStatement={taxTypeEmployeeStatement}
                    handleChange={handleChange}
                    validation={validation}
                    taxTypeOptions={TAX_TYPE_OPTIONS}
                    blurredElements={blurredElements}
                    setBlurred={setBlurred}
                    validUkAccSortCode={validUkAccSortCode}
                  />
                )}
                <div className="d-flex justify-content-end mt-2 mt-sm-0 flex-sm-row flex-column-reverse mx-1 mx-sm-0">
                  <Button
                    classes={`btn btn-outline-dark text-dark col-12 col-sm-3 col-lg-2 my-2 ${styles.payrollButton__cancel}`}
                    onClick={(e) => {
                      e.preventDefault();
                      history.push("/profile");
                    }}
                  >
                    Back to My Account
                  </Button>
                  {!isUpdating && (
                    <Button
                      id="submit-payroll-button"
                      classes="btn btn-primary mt-sm-2 mb-sm-2 ml-sm-2 col-12 col-sm-3 col-lg-2"
                      onClick={onSubmit}
                      disabled={
                        !taxTypeEmployeeStatement ||
                        hasErrors ||
                        (isDBSConsentVisible && dbsConsent === undefined)
                      }
                    >
                      {loading ? <Loading /> : "Save"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    </Validate>
  );
};

Payroll.propTypes = {
  loading: bool.isRequired,
  appData: object.isRequired,
  updateProfile: func.isRequired,
};

const mapStateToProps = ({ core: { app, loading } }) => ({
  appData: app,
  loading,
});

const mapDispatchToProps = { updateProfile: updateUserInProfileSilent };

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(Payroll);
